/**
 * Enrollment Card Component
 *
 * A card-based display for enrollment information, designed for parent-friendly viewing.
 * Shows class info, student, status, and payment information.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiUser,
} from "react-icons/fi"

import { EnrollmentActionsMenu } from "@/components/layout/Common/EnrollmentActionsMenu"
import type { EnrollmentResponse } from "@/types/enrollment"

// Status colors and labels
const statusConfig: Record<
  string,
  { color: string; icon: React.ReactNode; label: string }
> = {
  confirmed: {
    color: "green",
    icon: <FiCheckCircle />,
    label: "Confirmed",
  },
  pending: {
    color: "yellow",
    icon: <FiClock />,
    label: "Pending",
  },
  waitlisted: {
    color: "orange",
    icon: <FiClock />,
    label: "Waitlisted",
  },
  cancelled: {
    color: "red",
    icon: <FiAlertCircle />,
    label: "Cancelled",
  },
  completed: {
    color: "blue",
    icon: <FiCheckCircle />,
    label: "Completed",
  },
}

// Payment status colors
const paymentStatusConfig: Record<string, { color: string; label: string }> = {
  pending: { color: "orange", label: "Payment Due" },
  partial: { color: "yellow", label: "Partial Payment" },
  paid: { color: "green", label: "Paid" },
  refunded: { color: "gray", label: "Refunded" },
}

// Format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export interface EnrollmentCardProps {
  enrollment: EnrollmentResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function EnrollmentCard({
  enrollment,
  showActions = true,
  onClick,
}: EnrollmentCardProps) {
  const status = enrollment.status || "pending"
  const statusInfo = statusConfig[status] || statusConfig.pending
  const paymentStatus = enrollment.invoice?.payment_status || "pending"
  const paymentInfo =
    paymentStatusConfig[paymentStatus] || paymentStatusConfig.pending

  const amountDue = enrollment.invoice?.amount_due || 0
  const amountPaid = enrollment.invoice?.amount_paid || 0
  const balanceDue = amountDue - amountPaid

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with class name and status */}
          <Flex justify="space-between" align="start">
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {enrollment.class_name || "Swim Class"}
              </Text>
              {enrollment.session_name && (
                <Text fontSize="sm" color="gray.500">
                  {enrollment.session_name}
                </Text>
              )}
            </Box>

            <HStack gap={2}>
              <Badge
                colorPalette={statusInfo.color}
                variant="subtle"
                px={2}
                py={1}
                borderRadius="full"
              >
                <HStack gap={1}>
                  <Icon fontSize="xs">{statusInfo.icon}</Icon>
                  <Text>{statusInfo.label}</Text>
                </HStack>
              </Badge>

              {/* Actions menu */}
              {showActions && (
                <Box onClick={(e) => e.stopPropagation()}>
                  <EnrollmentActionsMenu enrollment={enrollment} />
                </Box>
              )}
            </HStack>
          </Flex>

          {/* Student info */}
          <HStack fontSize="sm" color="gray.600">
            <Icon color="gray.400">
              <FiUser />
            </Icon>
            <Text>{enrollment.student_name || "Student"}</Text>
          </HStack>

          {/* Waitlist position */}
          {status === "waitlisted" && enrollment.waitlist_position && (
            <Box
              bg="orange.50"
              borderRadius="md"
              p={3}
              borderLeftWidth="3px"
              borderLeftColor="orange.400"
            >
              <HStack color="orange.600" fontSize="sm">
                <Icon>
                  <FiClock />
                </Icon>
                <Text fontWeight="medium">
                  Waitlist Position: #{enrollment.waitlist_position}
                </Text>
              </HStack>
              <Text fontSize="xs" color="orange.500" mt={1}>
                You'll be notified when a spot opens up
              </Text>
            </Box>
          )}

          {/* Payment info */}
          <Box
            bg={balanceDue > 0 ? "orange.50" : "green.50"}
            borderRadius="md"
            p={3}
          >
            <Flex justify="space-between" align="center">
              <HStack color={balanceDue > 0 ? "orange.600" : "green.600"}>
                <Icon>
                  <FiCreditCard />
                </Icon>
                <Text fontSize="sm" fontWeight="medium">
                  {paymentInfo.label}
                </Text>
              </HStack>
              <VStack align="end" gap={0}>
                {balanceDue > 0 ? (
                  <>
                    <Text fontSize="lg" fontWeight="bold" color="orange.600">
                      {formatCurrency(balanceDue)}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      of {formatCurrency(amountDue)} due
                    </Text>
                  </>
                ) : (
                  <Text fontSize="lg" fontWeight="bold" color="green.600">
                    {formatCurrency(amountPaid)}
                  </Text>
                )}
              </VStack>
            </Flex>
          </Box>

          {/* Registration date */}
          <HStack fontSize="xs" color="gray.400" justify="flex-end">
            <Icon>
              <FiCalendar />
            </Icon>
            <Text>
              Enrolled {new Date(enrollment.created_at).toLocaleDateString()}
            </Text>
          </HStack>

          {/* Notes */}
          {enrollment.notes && (
            <Text fontSize="sm" color="gray.500" lineClamp={2}>
              {enrollment.notes}
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default EnrollmentCard
